import { zodResolver } from "@hookform/resolvers/zod";
import { addDays } from "date-fns";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetCustomV1 } from "@/api/customs.api";
import { MIN_DAYS_DEFERRED_SHIPMENT } from "@/constants/miscConstants";
import Paths from "@/constants/paths";
import useMediaQuery from "@/hooks/useMediaQuery";
import {
  GetCustomsV1Response,
  GetOneExpeditionResponseDto,
  GetPreferenceResponse,
} from "@/types/api/api-types";
import { CotationFormData, CotationSchema } from "@/types/forms/home/cotationFormData.form";
import DapOptionCard from "./DapOptionCard";
import ExpressCard from "./ExpressCard";
import InsuranceCard from "./InsuranceCard";
import PriceDetailCardDesktop from "./PriceDetailCard/PriceDetailCard.desktop";
import PriceDetailCardMobile from "./PriceDetailCard/PriceDetailCard.mobile";
import { ProductCard } from "./ProductCard";
import SaveCotationModal from "./SaveCotationModal";
import WoodBoxCard from "./WoodBoxCard";
import { toast } from "react-toastify";
import { RotateCcw } from "lucide-react";
import StyledButton from "@/components/styled/StyledButton";
import { useOtherFeesAutoUpdate } from "@/hooks/useOtherFeesAutoUpdate";

interface CotationsPageComponentProps {
  handleSubmit: (data: CotationFormData) => Promise<void>;
  initValues?: GetOneExpeditionResponseDto;
  preference: GetPreferenceResponse;
  isPending: boolean;
  customs: GetCustomsV1Response;
}

export const CotationsPageComponent = ({
  handleSubmit,
  initValues,
  preference,
  isPending,
  customs,
}: CotationsPageComponentProps) => {
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const navigate = useNavigate();
  const [resetCount, setResetCount] = useState(0);

  const methods = useForm<CotationFormData>({
    resolver: zodResolver(CotationSchema),
    defaultValues: {
      country: initValues?.quotation?.customId,
      products: initValues?.quotation?.quotationProducts
        ? initValues.quotation.quotationProducts.map((product) => {
            return {
              volume: product.volume,
              bottleQuantity: product.bottleQuantity,
              value: product.value,
            };
          })
        : [
            {
              bottleQuantity: undefined as unknown as number,
              value: undefined as unknown as number,
              volume: 0.75,
              isNew: true,
            },
          ],
      insurance:
        initValues?.expeditionOptions?.insurance !== undefined
          ? initValues?.expeditionOptions?.insurance
          : preference?.insuranceToggleDefault || false,
      woodBoxes: initValues?.quotation?.quotationWoodBox
        ? initValues.quotation.quotationWoodBox.map((woodBox) => ({
            bottleVolume: woodBox.bottleVolume,
            bottleQuantity: woodBox.bottleQuantity,
            boxQuantity: woodBox.boxQuantity,
          }))
        : [],
      express:
        initValues?.expeditionOptions?.express !== undefined
          ? initValues?.expeditionOptions?.express
          : false,
      dapOption:
        initValues?.expeditionOptions?.dapOption !== undefined
          ? initValues?.expeditionOptions?.dapOption
          : preference?.optionDapToggle || false,
      otherFeesToggle:
        initValues?.quotation?.otherFees !== undefined
          ? true
          : preference?.otherFeesToggle || false,
      otherFees: initValues?.quotation?.otherFees || 0,
      woodBoxesActivated: initValues?.quotation?.quotationWoodBox
        ? initValues?.quotation?.quotationWoodBox?.length > 0
          ? true
          : false
        : preference?.woodBoxesToggle || false,
      customer: {
        firstName: initValues?.deliveryInfo?.firstName || "",
        lastName: initValues?.deliveryInfo?.lastName || "",
        companyName: initValues?.deliveryInfo?.company || "",
      },
      deliveryDateRadio: initValues?.deliveryInfo?.deliveryFromDate
        ? "customDelivery"
        : "earliestDelivery",
      customDeliveryDate: initValues?.deliveryInfo?.deliveryFromDate
        ? new Date(initValues.deliveryInfo.deliveryFromDate)
        : addDays(new Date(), MIN_DAYS_DEFERRED_SHIPMENT),
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const customWatch = methods.watch("country");
  const otherFeesToggleWatch = methods.watch("otherFeesToggle");

  const { data: selectedCustom } = useGetCustomV1(customWatch || "");

  useEffect(() => {
    if ("country" in methods.formState.dirtyFields) {
      if (selectedCustom?.label.includes("DDP")) {
        methods.setValue("dapOption", false);
      } else if (selectedCustom?.label.includes("DAP")) {
        methods.setValue("dapOption", true);
      } else {
        methods.setValue("dapOption", preference?.optionDapToggle || false);
      }
    }
  }, [selectedCustom, preference, methods]);

  useOtherFeesAutoUpdate({
    preference,
    custom: selectedCustom,
    otherFeesToggle: otherFeesToggleWatch,
    cotationForm: methods,
  });

  const isLarge = useMediaQuery("(min-width: 768px)");

  const handleOpenSaveModal = async () => {
    if (
      (await methods.trigger([
        "country",
        "products",
        "insurance",
        "woodBoxes",
        "express",
        "dapOption",
        "woodBoxesActivated",
      ])) === false
    )
      return;

    setIsSaveModalOpen(true);
  };

  const handleCloseSaveModal = () => {
    setIsSaveModalOpen(false);
  };

  const handleReset = () => {
    methods.reset();
    setResetCount(resetCount + 1);
  };

  const onSubmit = async () => {
    methods.handleSubmit(
      async (data) => {
        await handleSubmit(data);
        if (data.deliveryDateRadio === "customDelivery") {
          toast.success(
            "Merci de respecter la date d’enlèvement qui se placera automatiquement quelques jours avant dans votre onglet « Validées ».",
          );
        }
        handleCloseSaveModal();
        if (!initValues) handleReset();
      },
      (_error) => {},
    )();
  };

  return (
    <FormProvider {...methods}>
      <div className="mb-28 flex h-fit flex-1 flex-col gap-8 overflow-hidden p-8 md:mb-0">
        <div className="flex flex-col items-start justify-between gap-2 md:flex-row md:items-center">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-semibold">
              {initValues ? "Modifier la" : "Nouvelle"} cotation
            </h1>
            <p className="">Renseignez les informations de votre cotation</p>
          </div>
          <div className="flex w-fit items-stretch gap-2">
            <button
              className="font-base bg-brand700 hover:bg-brand600 flex w-fit min-w-max flex-row items-center gap-2 px-3 py-2 text-sm text-white md:text-base lg:px-4 lg:py-3 lg:font-semibold"
              onClick={() => navigate(Paths.home.customCotations)}
            >
              Demandes spécifiques
            </button>
          </div>
        </div>
        <div className="flex flex-1 flex-col items-stretch md:gap-14 xl:flex-row">
          <div className="flex flex-1 flex-col gap-6 xl:min-w-[600px]">
            <ProductCard
              resetCount={resetCount}
              customs={customs}
              selectedCustom={selectedCustom || undefined}
            />
            <DapOptionCard selectedCustom={selectedCustom} />
            <WoodBoxCard
              defaultBottleQuantity={preference?.woodBoxesDefault || 12}
              woodboxDefault={preference?.woodBoxesDefault || 6}
              hasInitValue={!!initValues}
            />
            <ExpressCard />
            <InsuranceCard />
            <div className="flex flex-col gap-4 md:hidden">
              <StyledButton
                mode="secondary"
                onClick={handleReset}
                className="group flex flex-row justify-center gap-2"
              >
                <RotateCcw className="text-blue-600 transition duration-700 ease-in-out group-hover:-rotate-180" />
                Réinitialiser
              </StyledButton>
            </div>
          </div>
          <PriceDetailCardDesktop
            onSubmit={handleOpenSaveModal}
            reset={handleReset}
            showWinePrice={preference?.winePriceDisplayToggle || false}
          />
        </div>
      </div>
      {!isLarge && (
        <PriceDetailCardMobile
          onSubmit={handleOpenSaveModal}
          showWinePrice={preference?.winePriceDisplayToggle || false}
        />
      )}
      <SaveCotationModal
        open={isSaveModalOpen}
        handleClose={handleCloseSaveModal}
        onSubmit={onSubmit}
        isPending={isPending}
      />
    </FormProvider>
  );
};
